import { Command } from 'cmdk';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  LayoutDashboard,
  MessageSquare,
  Puzzle,
  Bot,
  Plug,
  Store,
  Settings,
  Plus,
  Workflow,
  LayoutTemplate,
  Rocket,
  FolderKanban,
  Wand2,
  type LucideIcon,
} from 'lucide-react';
import { api } from '@/lib/api/client';
import './command-palette.css';

// ---------------------------------------------------------------------------
// Static data
// ---------------------------------------------------------------------------

interface NavItem {
  id: string;
  label: string;
  path: string;
  icon: LucideIcon;
  keywords: string[];
}

const NAV_ITEMS: NavItem[] = [
  { id: 'nav-dashboard', label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard, keywords: ['home', 'overview'] },
  { id: 'nav-chat', label: 'Chat', path: '/dashboard/chat', icon: MessageSquare, keywords: ['ai', 'assistant', 'message'] },
  { id: 'nav-workflows', label: 'Workflows', path: '/dashboard/workflows', icon: Workflow, keywords: ['automation', 'list'] },
  { id: 'nav-builder', label: 'Workflow Builder', path: '/dashboard/workflows/builder', icon: Wand2, keywords: ['create', 'new', 'visual'] },
  { id: 'nav-templates', label: 'Templates', path: '/dashboard/templates', icon: LayoutTemplate, keywords: ['starter', 'preset'] },
  { id: 'nav-skills', label: 'Skills', path: '/dashboard/skills', icon: Puzzle, keywords: ['tools', 'plugins'] },
  { id: 'nav-agents', label: 'Agents', path: '/dashboard/agents', icon: Bot, keywords: ['bots', 'ai'] },
  { id: 'nav-projects', label: 'Projects', path: '/dashboard/projects', icon: FolderKanban, keywords: ['groups', 'organize'] },
  { id: 'nav-connectors', label: 'Connectors', path: '/dashboard/connectors', icon: Plug, keywords: ['integrations', 'connect'] },
  { id: 'nav-marketplace', label: 'Marketplace', path: '/dashboard/marketplace', icon: Store, keywords: ['browse', 'install'] },
  { id: 'nav-onboarding', label: 'Onboarding', path: '/dashboard/onboarding', icon: Rocket, keywords: ['setup', 'getting started', 'wizard'] },
  { id: 'nav-settings', label: 'Settings', path: '/dashboard/settings', icon: Settings, keywords: ['account', 'profile', 'preferences'] },
];

interface ActionItem {
  id: string;
  label: string;
  path: string;
  icon: LucideIcon;
  keywords: string[];
}

const ACTION_ITEMS: ActionItem[] = [
  { id: 'action-new-workflow', label: 'Create New Workflow', path: '/dashboard/workflows/builder', icon: Plus, keywords: ['add', 'new', 'workflow'] },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const navigate = useNavigate();

  // Fetch workflows only when palette is open
  const { data: workflowsData } = useQuery({
    queryKey: ['workflows-palette'],
    queryFn: () => api.getWorkflows({ limit: 50 }),
    enabled: open,
    staleTime: 30_000, // Cache for 30s to avoid refetch on quick reopen
  });

  const workflows = useMemo(() => {
    const list = workflowsData?.data?.workflows ?? [];
    return list.map((w) => ({
      id: `wf-${w.id}`,
      workflowId: w.id,
      label: w.name,
      description: w.description ?? undefined,
    }));
  }, [workflowsData]);

  function handleSelect(path: string) {
    navigate(path);
    onOpenChange(false);
  }

  return (
    <Command.Dialog
      open={open}
      onOpenChange={onOpenChange}
      label="Command palette"
      loop
    >
      <Command.Input placeholder="Type a command or search..." />
      <Command.List>
        <Command.Empty>No results found.</Command.Empty>

        {/* Navigation */}
        <Command.Group heading="Navigation">
          {NAV_ITEMS.map((item) => (
            <Command.Item
              key={item.id}
              value={item.label}
              keywords={item.keywords}
              onSelect={() => handleSelect(item.path)}
            >
              <item.icon className="w-4 h-4 shrink-0 text-[var(--volt-text-muted)]" />
              <span>{item.label}</span>
            </Command.Item>
          ))}
        </Command.Group>

        {/* Quick actions */}
        <Command.Group heading="Actions">
          {ACTION_ITEMS.map((item) => (
            <Command.Item
              key={item.id}
              value={item.label}
              keywords={item.keywords}
              onSelect={() => handleSelect(item.path)}
            >
              <item.icon className="w-4 h-4 shrink-0 text-[var(--volt-text-muted)]" />
              <span>{item.label}</span>
            </Command.Item>
          ))}
        </Command.Group>

        {/* Dynamic workflows */}
        {workflows.length > 0 && (
          <Command.Group heading="Workflows">
            {workflows.map((w) => (
              <Command.Item
                key={w.id}
                value={w.label}
                keywords={['workflow', ...(w.description ? [w.description] : [])]}
                onSelect={() => handleSelect(`/dashboard/workflows/builder/${w.workflowId}`)}
              >
                <Workflow className="w-4 h-4 shrink-0 text-[var(--volt-text-muted)]" />
                <span>{w.label}</span>
                {w.description && (
                  <span className="cmdk-item-description">{w.description}</span>
                )}
              </Command.Item>
            ))}
          </Command.Group>
        )}
      </Command.List>
    </Command.Dialog>
  );
}
